import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabase/supabase';
import { useCRM } from './CRMContext';
import { useOutreach } from './OutreachContext';
import { generateId } from '../utils/helpers';

const EmailContext = createContext();

export const useEmail = () => useContext(EmailContext);

export const EmailProvider = ({ children }) => {
  const { currentUser, addActivity, permissions, projects, deals, companies, emailTemplates, addEmailTemplate, updateEmailTemplate } = useCRM();
  const { generateTrackedLink } = useOutreach();

  // --- STATE ---
  const [drafts, setDrafts] = useState([]);
  const [settings, setSettings] = useState({
    require_human_send: true,
    autopilot_enabled: false,
    spf_verified: false,
    dkim_verified: false,
    daily_limit_per_user: 50,
    default_footer: "PS: Si ifra om dette ikke er relevant, så tar jeg deg av listen."
  });
  const [dncList, setDncList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [queue, setQueue] = useState([]);
  
  // Template Import State
  const [templateImportJobs, setTemplateImportJobs] = useState([]);

  // --- MOCK INIT ---
  useEffect(() => {
    // Load Settings (Mock)
    if (permissions.canEmailAutopilot) {
      setSettings(prev => ({ ...prev, spf_verified: true, dkim_verified: true }));
    }
    // Load DNC Mock
    setDncList(['nei@takk.no', 'aldri@mer.com']);
  }, [permissions]);

  // --- ACTIONS ---
  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const addToDNC = (email) => {
    setDncList(prev => [...prev, email]);
  };

  const checkDNC = (email) => {
    if (!email) return false;
    return dncList.includes(email);
  };

  // --- AI GENERATION LOGIC ---
  const generateDrafts = async (contextData) => {
    // ... existing implementation
    setLoading(true);
    const { contact, dealId, projectId, companyId } = contextData;

    // 1. Compliance Check
    if (checkDNC(contact.email)) {
      setLoading(false);
      throw new Error(`E-postadresse ${contact.email} er blokkert (DNC).`);
    }

    // 2. Gather Context (Pitch/Battlecards)
    let aiContext = "Generell B2B intro.";
    if (projectId) {
      const project = projects.find(p => p.id === projectId);
      if (project?.content?.pitch) {
        aiContext = `Pitch: ${project.content.pitch}`;
      }
    }

    // 3. Simulate AI Generation (3 Variants)
    await new Promise(r => setTimeout(r, 1500)); // Latency

    const variants = {
      short: {
        subject: `Kjapt spm: ${contact.name}`,
        body: `Hei ${contact.name.split(' ')[0]},\n\nHar dere vurdert å oppgradere prosessene deres?\n\n${aiContext.substring(0, 50)}...\n\nMvh,\n${currentUser.name}`
      },
      normal: {
        subject: `Samarbeid med ${companies.find(c => c.company_id === companyId)?.name || 'dere'}?`,
        body: `Hei ${contact.name},\n\nJeg kontakter deg fordi vi ser store muligheter.\n\n${aiContext}\n\nLa oss ta en prat.\n\nMvh,\n${currentUser.name}\n\n${settings.default_footer}`
      },
      direct: {
        subject: `Forslag til løsning`,
        body: `Hei ${contact.name},\n\nRett på sak: Vi kan hjelpe dere.\n\n${aiContext}\n\nEr dette relevant?\n\nBest,\n${currentUser.name}`
      }
    };

    const newDraft = {
      draft_id: generateId('draft'),
      user_id: currentUser.user_id,
      created_at: new Date().toISOString(),
      status: 'Klar til send',
      project_id: projectId,
      deal_id: dealId,
      company_id: companyId,
      contact_id: contact.contact_id,
      to_email: contact.email,
      to_name: contact.name,
      variants,
      selected_variant: 'normal',
      subject: variants.normal.subject,
      body: variants.normal.body
    };

    setDrafts(prev => [newDraft, ...prev]);
    setLoading(false);
    return newDraft;
  };

  // --- LINK TRACKING ---
  const attachTrackedLink = (draftId, url) => {
    // ... existing implementation
    const draft = drafts.find(d => d.draft_id === draftId);
    if (!draft) return;
    const trackedUrl = generateTrackedLink(url, null, null, null, null);
    const updatedBody = draft.body + `\n\nLink: ${trackedUrl}`;
    setDrafts(prev => prev.map(d => d.draft_id === draftId ? { 
      ...d, 
      tracked_url: trackedUrl, 
      body: updatedBody,
      variants: {
        ...d.variants,
        [d.selected_variant]: { ...d.variants[d.selected_variant], body: updatedBody }
      }
    } : d));
  };

  // --- SENDING LOGIC ---
  const markAsSentManual = (draftId) => {
    // ... existing implementation
    const draft = drafts.find(d => d.draft_id === draftId);
    if (!draft) return;
    setDrafts(prev => prev.map(d => d.draft_id === draftId ? { ...d, status: 'Sendt manuelt' } : d));
    addActivity({
      type: 'E-post',
      title: `E-post sendt manuelt: ${draft.subject}`,
      description: draft.body,
      company_id: draft.company_id,
      contact_id: draft.contact_id,
      deal_id: draft.deal_id,
      status: 'Gjennomført',
      date: new Date().toISOString()
    });
  };

  const sendViaAutopilot = async (draftId) => {
    // ... existing implementation
    if (!settings.autopilot_enabled) throw new Error("Autopilot er ikke aktivert.");
    if (!settings.spf_verified || !settings.dkim_verified) throw new Error("Domene er ikke verifisert (SPF/DKIM).");
    
    const draft = drafts.find(d => d.draft_id === draftId);
    const queueItem = {
      queue_id: generateId('q'),
      draft_id: draftId,
      status: 'Pending',
      scheduled_for: new Date().toISOString()
    };
    
    setQueue(prev => [...prev, queueItem]);

    setTimeout(() => {
      setQueue(prev => prev.map(q => q.queue_id === queueItem.queue_id ? { ...q, status: 'Sent' } : q));
      setDrafts(prev => prev.map(d => d.draft_id === draftId ? { ...d, status: 'Sendt (Autopilot)' } : d));
      addActivity({
        type: 'E-post',
        title: `Autopilot E-post: ${draft.subject}`,
        description: `Sendt automatisk.\n\n${draft.body}`,
        company_id: draft.company_id,
        contact_id: draft.contact_id,
        deal_id: draft.deal_id,
        status: 'Gjennomført',
        date: new Date().toISOString()
      });
    }, 2000);
  };

  // --- TEMPLATE IMPORT LOGIC (V1) ---

  const parseTemplateCSV = (text) => {
    // Basic CSV parser holding quotes roughly
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) return [];
    
    // Simplistic split by comma, handling quoted sections would need regex
    // For this demo: assumes standard CSV without complex internal commas
    const headers = lines[0].split(',').map(h => h.trim());
    
    return lines.slice(1).map((line, idx) => {
      // Split by comma but respect quotes
      const values = [];
      let inQuote = false;
      let currentVal = '';
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuote = !inQuote;
        } else if (char === ',' && !inQuote) {
          values.push(currentVal.trim());
          currentVal = '';
        } else {
          currentVal += char;
        }
      }
      values.push(currentVal.trim());

      const row = { row_no: idx + 1 };
      headers.forEach((h, i) => {
        let val = values[i] || '';
        // Remove surrounding quotes if present
        if (val.startsWith('"') && val.endsWith('"')) val = val.slice(1, -1);
        // Restore newlines
        val = val.replace(/\\n/g, '\n'); 
        row[h] = val;
      });
      return row;
    });
  };

  const createTemplateImportJob = async (csvText, options) => {
    const jobId = generateId('job-tmpl');
    const rawRows = parseTemplateCSV(csvText);

    const validPurposes = ["Første kontakt", "Oppfølging", "Møtebooking", "Breakup", "Reaktivering"];
    const validTones = ["Kort", "Normal", "Direkte"];
    const validCtas = ["Book møte", "Svar med 1–2 linjer", "Se microsite", "Last ned"];

    const rows = rawRows.map(row => {
      const errors = [];
      if (!row.project_id) errors.push('Mangler project_id');
      if (!row.name) errors.push('Mangler name');
      if (!row.subject_template) errors.push('Mangler subject_template');
      if (!row.body_template) errors.push('Mangler body_template');
      
      if (row.purpose && !validPurposes.includes(row.purpose)) errors.push(`Ugyldig purpose: ${row.purpose}`);
      if (row.tone && !validTones.includes(row.tone)) errors.push(`Ugyldig tone: ${row.tone}`);
      // CTA validation optional as it might be custom
      
      return {
        ...row,
        row_id: generateId('row'),
        job_id: jobId,
        validation_status: errors.length > 0 ? 'FEIL' : 'OK',
        validation_errors: errors.join('; ')
      };
    });

    const summary = {
      rows_total: rows.length,
      rows_valid: rows.filter(r => r.validation_status === 'OK').length,
      rows_invalid: rows.filter(r => r.validation_status === 'FEIL').length,
      templates_created: 0,
      templates_updated: 0
    };

    const newJob = {
      job_id: jobId,
      created_at: new Date().toISOString(),
      status: 'Validert',
      csv_text: csvText,
      options,
      summary,
      rows
    };

    setTemplateImportJobs(prev => [newJob, ...prev]);
    return newJob;
  };

  const runTemplateImportJob = async (jobId) => {
    const job = templateImportJobs.find(j => j.job_id === jobId);
    if (!job) return;

    setTemplateImportJobs(prev => prev.map(j => j.job_id === jobId ? { ...j, status: 'Kjører' } : j));

    let createdCount = 0;
    let updatedCount = 0;

    const validRows = job.rows.filter(r => r.validation_status === 'OK');

    for (const row of validRows) {
      // Lookup existing template based on name OR project_id+name combination
      // For this mock, we assume unique names within the system or project
      const existing = emailTemplates.find(t => t.name === row.name && (!row.project_id || t.project_id === row.project_id));
      
      if (existing) {
        if (job.options.mode === 'Upsert') {
          const updates = { ...existing };
          // Always update metadata fields
          updates.purpose = row.purpose || existing.purpose;
          updates.tone = row.tone || existing.tone;
          updates.cta_style = row.cta_style || existing.cta_style;
          if (row.project_id) updates.project_id = row.project_id;
          
          // Conditional updates based on options
          if (job.options.overwrite_subject_template) updates.subject = row.subject_template;
          if (job.options.overwrite_body_template) updates.body = row.body_template;
          
          updateEmailTemplate(existing.id, updates);
          updatedCount++;
        }
        // If CreateOnly, skip
      } else {
        const newTmpl = {
          name: row.name,
          subject: row.subject_template,
          body: row.body_template,
          owner: currentUser.name,
          // Extra meta fields
          purpose: row.purpose,
          tone: row.tone,
          cta_style: row.cta_style,
          project_id: row.project_id
        };
        addEmailTemplate(newTmpl);
        createdCount++;
      }
    }

    setTemplateImportJobs(prev => prev.map(j => j.job_id === jobId ? { 
      ...j, 
      status: 'Fullført',
      summary: { ...j.summary, templates_created: createdCount, templates_updated: updatedCount }
    } : j));
    
    // Log audit
    console.log(`[AUDIT] Template Import Completed: ${createdCount} created, ${updatedCount} updated.`);
  };

  return (
    <EmailContext.Provider value={{
      drafts,
      settings,
      dncList,
      loading,
      queue,
      templateImportJobs, // Export new state
      updateSettings,
      addToDNC,
      checkDNC,
      generateDrafts,
      attachTrackedLink,
      markAsSentManual,
      sendViaAutopilot,
      // Export new functions
      createTemplateImportJob,
      runTemplateImportJob
    }}>
      {children}
    </EmailContext.Provider>
  );
};